import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDocument,
  deleteDocument,
  getDocumentDownloadUrl,
  getDocumentUploadUrl,
  listDocuments,
  type DocType,
  type VaultDocument,
} from "../lib/api-modules/documents.js";
import {
  Button,
  Card,
  DateField,
  EmptyState,
  Pill,
  SectionHeader,
  SelectField,
  TextField,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /documents — Phase 10 (2026-05-08).
 *
 * General documents vault. Distinct from `/estate` (Phase 9, estate-
 * specific documents) and `/tax` (Phase 7.1, IRS forms). Houses
 * passports, deeds, vehicle titles, school records, etc.
 *
 * The "Expiring within 90 days" tile uses the same query the weekly
 * `document-expiration-audit` cron runs.
 */

const DOC_TYPE_OPTIONS: { value: DocType; label: string }[] = [
  { value: "passport", label: "Passport" },
  { value: "birth_certificate", label: "Birth certificate" },
  { value: "marriage_certificate", label: "Marriage certificate" },
  { value: "divorce_decree", label: "Divorce decree" },
  { value: "green_card", label: "Green card" },
  { value: "social_security_card", label: "Social Security card" },
  { value: "drivers_license", label: "Driver's license" },
  { value: "vehicle_title", label: "Vehicle title" },
  { value: "vehicle_registration", label: "Vehicle registration" },
  { value: "deed", label: "Deed" },
  { value: "ho6_share_certificate", label: "HO-6 share certificate" },
  { value: "tax_form", label: "Tax form" },
  { value: "medical_record", label: "Medical record" },
  { value: "vaccination_record", label: "Vaccination record" },
  { value: "school_record", label: "School record" },
  { value: "military_dd214", label: "Military DD-214" },
  { value: "hsa_receipt", label: "HSA receipt" },
  { value: "other", label: "Other" },
];

const ALLOWED_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function inferContentType(filename: string): string | null {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (!ext) return null;
  return ALLOWED_CONTENT_TYPES[ext] ?? null;
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, error, isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: () => listDocuments(),
  });
  const [showAdd, setShowAdd] = useState(false);
  const [filterType, setFilterType] = useState<DocType | "">("");
  const deleteM = useMutation({
    mutationFn: deleteDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.show("Document deleted");
    },
    onError: (err) => toast.show((err).message),
  });

  const items = data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + 90);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  const expiring = useMemo(
    () =>
      items.filter(
        (d) =>
          d.expiration_date &&
          d.expiration_date >= today &&
          d.expiration_date <= cutoffIso,
      ),
    [items, today, cutoffIso],
  );
  const filtered = useMemo(() => {
    if (!filterType) return items;
    return items.filter((d) => d.doc_type === filterType);
  }, [items, filterType]);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <SectionHeader
        title="Documents vault"
        sub="Encrypted permanent home for passports, deeds, IDs, receipts, and other records that need to be findable. Expiration alerts ship every Saturday for anything within 90 days."
      />

      {expiring.length > 0 && (
        <Card className="mb-4 border-[var(--color-warning)]/40 bg-[var(--color-warning-soft)]/30">
          <p className="text-sm font-medium text-[var(--color-warning)]">
            {expiring.length} document(s) expiring within 90 days
          </p>
          <ul className="mt-2 space-y-1 text-xs text-[var(--color-text)]">
            {expiring.map((d) => (
              <li key={d.doc_id}>
                • {d.display_name} — expires {d.expiration_date}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <SelectField
          label="Filter by type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as DocType | "")}
        >
          <option value="">All types</option>
          {DOC_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectField>
        <Button onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "Add document"}
        </Button>
      </div>

      {showAdd && (
        <div className="mb-4">
          <AddDocumentForm onCreated={() => setShowAdd(false)} />
        </div>
      )}

      {isLoading && <p className="text-sm">Loading documents…</p>}
      {error && (
        <p className="text-sm text-[var(--color-danger)]">{error.message}</p>
      )}
      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          title="No documents yet"
          description="Add a passport, birth certificate, deed, or any other document you'd want to find quickly. Files are encrypted at rest."
        />
      )}
      {!isLoading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((d) => (
            <DocumentRow
              key={d.doc_id}
              doc={d}
              onDelete={() => deleteM.mutate(d.doc_id)}
              isDeleting={deleteM.isPending && deleteM.variables === d.doc_id}
              onError={(msg) => toast.show(msg)}
            />
          ))}
        </div>
      )}
      <Toast message={toast.message} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.documents} />
      </div>
    </div>
  );
}

function DocumentRow({
  doc,
  onDelete,
  isDeleting,
  onError,
}: {
  doc: VaultDocument;
  onDelete: () => void;
  isDeleting: boolean;
  onError: (msg: string) => void;
}) {
  const onDownload = async () => {
    try {
      const resp = await getDocumentDownloadUrl(doc.doc_id);
      window.open(resp.download_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      onError((err as Error).message);
    }
  };
  const docTypeLabel =
    DOC_TYPE_OPTIONS.find((o) => o.value === doc.doc_type)?.label ?? doc.doc_type;
  const today = new Date().toISOString().slice(0, 10);
  const isExpired = doc.expiration_date && doc.expiration_date < today;
  return (
    <Card className="flex flex-wrap items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[var(--color-text)]">
            {doc.display_name}
          </span>
          <Pill tone="neutral">{docTypeLabel}</Pill>
          {isExpired && <Pill tone="danger">Expired</Pill>}
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          {doc.expiration_date ? `Expires ${doc.expiration_date}` : "No expiration"}
          {doc.s3_object_key ? " · file uploaded" : " · no file uploaded"}
          {doc.original_filename ? ` · ${doc.original_filename}` : ""}
        </p>
      </div>
      {doc.s3_object_key && (
        <Button onClick={() => void onDownload()} variant="secondary" size="sm">
          Download
        </Button>
      )}
      <ConfirmButton
        label="Delete"
        confirmLabel="Confirm delete"
        onConfirm={onDelete}
        isPending={isDeleting}
      />
    </Card>
  );
}

function AddDocumentForm({ onCreated }: { onCreated: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [docType, setDocType] = useState<DocType>("passport");
  const [displayName, setDisplayName] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const create = useMutation({
    mutationFn: createDocument,
    onSuccess: async (doc) => {
      if (file) {
        try {
          setUploading(true);
          const ct = inferContentType(file.name);
          if (!ct) {
            toast.show("Unsupported file type — pick PDF / image / DOCX");
            return;
          }
          const presigned = await getDocumentUploadUrl(doc.doc_id, {
            filename: file.name,
            content_type: ct,
            byte_size: file.size,
          });
          const headers = new Headers();
          for (const [k, v] of Object.entries(presigned.required_headers)) {
            headers.set(k, v);
          }
          const putResp = await fetch(presigned.upload_url, {
            method: "PUT",
            headers,
            body: file,
          });
          if (!putResp.ok) {
            throw new Error(`S3 upload failed: ${putResp.status}`);
          }
        } catch (err) {
          toast.show(
            `Upload failed (metadata saved): ${(err as Error).message}`,
          );
        } finally {
          setUploading(false);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.show("Document added");
      onCreated();
    },
    onError: (err) => toast.show((err).message),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName) {
      toast.show("Display name is required");
      return;
    }
    create.mutate({
      doc_type: docType,
      display_name: displayName,
      ...(expirationDate ? { expiration_date: expirationDate } : {}),
    });
  };

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
        <SelectField
          label="Document type"
          value={docType}
          onChange={(e) => setDocType(e.target.value as DocType)}
        >
          {DOC_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g., Andre's passport"
        />
        <DateField
          label="Expiration date (optional)"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--color-muted)]">
            Upload file (optional)
          </label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-[var(--color-text)]"
          />
          <p className="text-[11px] text-[var(--color-muted)]">
            Encrypted at rest in the documents bucket. PDF / image / DOCX up to
            50MB.
          </p>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" disabled={create.isPending || uploading}>
            {create.isPending || uploading ? "Saving…" : "Save document"}
          </Button>
        </div>
      </form>
      <Toast message={toast.message} />
    </Card>
  );
}
